// modals/TaskItem.js - VERSION AMÉLIORÉE AVEC UX SOUS-TÂCHES ET BOUTONS STANDARDISÉS
import {
    Calendar,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Circle,
    Clock,
    Flag,
    GripVertical,
    Plus,
    Trash2,
    X
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TaskItem = ({
    task,
    taskIndex,
    isExpanded,
    onTaskChange,
    onSubtaskChange,
    onRemoveTask,
    onAddSubtask,
    onRemoveSubtask,
    onToggleExpansion,
    onTaskDragStart,
    onTaskDragOver,
    onTaskDrop,
    onSubtaskDragStart,
    onSubtaskDragOver,
    onSubtaskDrop,
    onDragEnd,
    dragState,
    errors,
    canRemove,
    disabled = false
}) => {
    const [showPriorityDropdown, setShowPriorityDropdown] = useState({ taskIndex: null });

    // CORRECTION: Nouveaux statuts conformes à la base de données - design sobre
    const statusOptions = [
        {
            value: 'a_faire',
            label: 'À faire',
            icon: Circle,
            color: 'text-gray-600',
            bgColor: 'bg-gray-100',
            borderColor: 'border-gray-200'
        },
        {
            value: 'en_cours',
            label: 'En cours',
            icon: Clock,
            color: 'text-gray-600',
            bgColor: 'bg-gray-100',
            borderColor: 'border-gray-200'
        },
        {
            value: 'fait',
            label: 'Fait',
            icon: CheckCircle2,
            color: 'text-gray-600',
            bgColor: 'bg-gray-100',
            borderColor: 'border-gray-200'
        },
        {
            value: 'annule',
            label: 'Annulé',
            icon: X,
            color: 'text-gray-600',
            bgColor: 'bg-gray-100',
            borderColor: 'border-gray-200'
        }
    ];

    // AMÉLIORATION: Priorités avec design sobre et largeur standardisée
    const priorityOptions = [
        { value: 'low', label: 'Basse', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' },
        { value: 'medium', label: 'Moyenne', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' },
        { value: 'high', label: 'Haute', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' }
    ];

    const { draggedItem, dragOverItem, dragType } = dragState;
    const isTaskDragging = dragType === 'task' && draggedItem?.taskIndex === taskIndex;
    const isTaskDragOver = dragType === 'task' && dragOverItem?.taskIndex === taskIndex;

    function StatusSelector({ status, onChange, size = 'normal' }) {
        const [isOpen, setIsOpen] = useState(false);
        const buttonRef = useRef(null);
        const [buttonRect, setButtonRect] = useState(null);

        // CORRECTION: Gérer le cas où le statut n'existe pas dans les nouvelles options
        const currentStatus = statusOptions.find(s => s.value === status) || statusOptions[0]; // a_faire par défaut
        const IconComponent = currentStatus.icon;

        useEffect(() => {
            if (isOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setButtonRect(rect);
            }
        }, [isOpen]);

        useEffect(() => {
            const handleScroll = () => {
                if (isOpen) setIsOpen(false);
            };
            if (isOpen) {
                window.addEventListener('scroll', handleScroll, true);
                return () => window.removeEventListener('scroll', handleScroll, true);
            }
        }, [isOpen]);

        const dropdown = isOpen && buttonRect && createPortal(
            <>
                <div
                    className="fixed inset-0 z-[25000]"
                    onClick={() => setIsOpen(false)}
                    data-status-dropdown
                />
                <div
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[25001] min-w-32"
                    data-status-dropdown
                    style={{
                        left: Math.min(buttonRect.left, window.innerWidth - 140),
                        top: buttonRect.bottom + 4 > window.innerHeight - 120
                            ? buttonRect.top - 120
                            : buttonRect.bottom + 4
                    }}
                >
                    {statusOptions.map(option => {
                        const OptionIcon = option.icon;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    if (!disabled) {
                                        onChange(option.value);
                                        setIsOpen(false);
                                    }
                                }}
                                disabled={disabled}
                                className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${status === option.value ? 'bg-gray-100' : ''
                                    }`}
                            >
                                <OptionIcon className={`w-4 h-4 ${option.color}`} />
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            </>,
            document.body
        );

        return (
            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={(e) => {
                        if (!disabled) {
                            e.stopPropagation();
                            setIsOpen(!isOpen);
                        }
                    }}
                    disabled={disabled}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${currentStatus.bgColor
                        } ${currentStatus.borderColor} ${currentStatus.color} hover:shadow-sm ${size === 'small' ? 'text-xs w-20' : 'text-sm w-24'
                        }`}
                    title={currentStatus.label}
                >
                    <IconComponent className={size === 'small' ? 'w-3 h-3' : 'w-4 h-4'} />
                    {size === 'normal' && <span className="font-medium text-xs">{currentStatus.label}</span>}
                </button>
                {dropdown}
            </div>
        );
    }

    function PrioritySelector({ priority, onChange, taskIndex }) {
        const isOpen = showPriorityDropdown.taskIndex === taskIndex;
        const selectedPriority = priorityOptions.find(p => p.value === priority) || priorityOptions[1]; // medium par défaut
        const buttonRef = useRef(null);
        const [buttonRect, setButtonRect] = useState(null);

        useEffect(() => {
            if (isOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setButtonRect(rect);
            }
        }, [isOpen]);

        const dropdown = isOpen && buttonRect && createPortal(
            <>
                <div
                    className="fixed inset-0 z-[25000]"
                    onClick={() => setShowPriorityDropdown({ taskIndex: null })}
                    data-status-dropdown
                />
                <div
                    className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-[25001] min-w-36"
                    data-status-dropdown
                    style={{
                        left: Math.min(buttonRect.left, window.innerWidth - 150),
                        top: buttonRect.bottom + 4 > window.innerHeight - 140
                            ? buttonRect.top - 140
                            : buttonRect.bottom + 4
                    }}
                >
                    {priorityOptions.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                                if (!disabled) {
                                    onChange(option.value);
                                    setShowPriorityDropdown({ taskIndex: null });
                                }
                            }}
                            disabled={disabled}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${priority === option.value ? 'bg-gray-100' : ''
                                }`}
                        >
                            <div className={`w-2 h-2 rounded-full ${option.dotColor}`}></div>
                            <span className="font-medium">{option.label}</span>
                        </button>
                    ))}
                </div>
            </>,
            document.body
        );

        return (
            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => {
                        if (!disabled) {
                            setShowPriorityDropdown(isOpen ? { taskIndex: null } : { taskIndex });
                        }
                    }}
                    disabled={disabled}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed w-24 ${selectedPriority.color}`}
                    title={`Priorité: ${selectedPriority.label}`}
                >
                    <div className={`w-2 h-2 rounded-full ${selectedPriority.dotColor}`}></div>
                    <span className="text-xs">{selectedPriority.label}</span>
                    <ChevronDown className="w-3 h-3" />
                </button>
                {dropdown}
            </div>
        );
    }

    return (
        <div
            className={`border border-gray-200 rounded-lg overflow-hidden transition-all duration-150 ${isTaskDragging ? 'opacity-50 transform rotate-1 scale-98' : ''
                } ${isTaskDragOver ? 'ring-2 ring-teal-200 border-teal-300' : ''} ${disabled ? 'opacity-60' : ''
                }`}
            onDragOver={(e) => !disabled && onTaskDragOver(e, taskIndex)}
            onDrop={(e) => !disabled && onTaskDrop(e, taskIndex)}
            onDragEnd={onDragEnd}
        >
            {/* Task Header */}
            <div className="p-3 bg-gray-50">
                {/* Une seule ligne avec tout */}
                <div className="flex items-center gap-2">
                    <div
                        className={`cursor-move text-gray-400 hover:text-gray-600 p-1 flex-shrink-0 ${disabled ? 'cursor-not-allowed opacity-50' : ''
                            }`}
                        draggable={!disabled}
                        onDragStart={(e) => !disabled && onTaskDragStart(e, taskIndex)}
                    >
                        <GripVertical className="w-4 h-4" />
                    </div>

                    <div className="flex-shrink-0">
                        <StatusSelector
                            status={task.status || 'a_faire'} // CORRECTION: Statut par défaut
                            onChange={(value) => !disabled && onTaskChange(taskIndex, 'status', value)}
                        />
                    </div>

                    {/* TITRE ÉLARGI - Prend la place restante */}
                    <input
                        type="text"
                        value={task.title}
                        onChange={(e) => !disabled && onTaskChange(taskIndex, 'title', e.target.value)}
                        disabled={disabled}
                        className={`flex-1 min-w-0 px-3 py-1.5 text-sm font-medium border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${errors[`task_${taskIndex}_title`] ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                            }`}
                        placeholder="Titre de la tâche..."
                    />

                    <div className="flex items-center gap-1 bg-white rounded-md border border-gray-200 px-2 py-1 flex-shrink-0">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <input
                            type="date"
                            value={task.deadline}
                            onChange={(e) => !disabled && onTaskChange(taskIndex, 'deadline', e.target.value)}
                            disabled={disabled}
                            className="text-xs border-0 focus:ring-0 p-0 w-24 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                    </div>

                    <div className="flex-shrink-0">
                        <PrioritySelector
                            priority={task.priority || 'medium'} // CORRECTION: Priorité par défaut
                            onChange={(value) => !disabled && onTaskChange(taskIndex, 'priority', value)}
                            taskIndex={taskIndex}
                        />
                    </div>

                    {/* AMÉLIORATION: Indicateur visuel pour les sous-tâches */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {task.subtasks && task.subtasks.length > 0 && (
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                {task.subtasks.length}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => !disabled && onToggleExpansion(taskIndex)}
                            disabled={disabled}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white rounded-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </button>
                    </div>

                    {canRemove && (
                        <button
                            type="button"
                            onClick={() => !disabled && onRemoveTask(taskIndex)}
                            disabled={disabled}
                            className="p-1 text-gray-400 hover:text-red-500 rounded-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Trash2 className="w-3 h-3" />
                        </button>
                    )}
                </div>

                {errors[`task_${taskIndex}_title`] && (
                    <p className="mt-2 text-xs text-red-600">{errors[`task_${taskIndex}_title`]}</p>
                )}
            </div>

            {/* AMÉLIORATION: Section sous-tâches avec meilleure UX */}
            {isExpanded && (
                <div className="bg-white border-t border-gray-100">
                    {/* En-tête avec bouton d'ajout proéminent */}
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Sous-action</span>
                                {task.subtasks && task.subtasks.length > 0 && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
                                        {task.subtasks.length}
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => !disabled && onAddSubtask(taskIndex)}
                                disabled={disabled}
                                className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 rounded-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ backgroundColor: '#c8d7d6' }}
                            >
                                <Plus className="w-4 h-4 mr-1.5" />
                                Ajouter une sous-action
                            </button>
                        </div>
                    </div>

                    <div className="p-3">
                        {/* Zone d'aide si pas de sous-tâches */}
                        {(!task.subtasks || task.subtasks.length === 0) && (
                            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                <div className="text-gray-400 mb-3">
                                    <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">Décomposez cette action en étapes plus petites</p>
                                <button
                                    type="button"
                                    onClick={() => !disabled && onAddSubtask(taskIndex)}
                                    disabled={disabled}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 rounded-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: '#c8d7d6' }}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer ma première sous-action
                                </button>
                            </div>
                        )}

                        {/* Liste des sous-tâches */}
                        {task.subtasks && task.subtasks.length > 0 && (
                            <div className="space-y-2">
                                {task.subtasks.map((subtask, subtaskIndex) => {
                                    const isSubtaskDragging = dragType === 'subtask' && draggedItem?.taskIndex === taskIndex && draggedItem?.subtaskIndex === subtaskIndex;
                                    const isSubtaskDragOver = dragType === 'subtask' && dragOverItem?.taskIndex === taskIndex && dragOverItem?.subtaskIndex === subtaskIndex;

                                    return (
                                        <div
                                            key={subtask.id}
                                            className={`flex items-center gap-2 p-3 bg-gray-50 rounded-lg border transition-all duration-150 ${isSubtaskDragging ? 'opacity-50 transform rotate-1' : ''
                                                } ${isSubtaskDragOver ? 'bg-blue-50 border-blue-200 border-dashed' : 'border-gray-200'}`}
                                            onDragOver={(e) => !disabled && onSubtaskDragOver(e, taskIndex, subtaskIndex)}
                                            onDrop={(e) => !disabled && onSubtaskDrop(e, taskIndex, subtaskIndex)}
                                        >
                                            <div
                                                className={`cursor-move text-gray-400 hover:text-gray-600 p-1 ${disabled ? 'cursor-not-allowed opacity-50' : ''
                                                    }`}
                                                draggable={!disabled}
                                                onDragStart={(e) => !disabled && onSubtaskDragStart(e, taskIndex, subtaskIndex)}
                                                onDragEnd={onDragEnd}
                                            >
                                                <GripVertical className="w-3 h-3" />
                                            </div>

                                            <StatusSelector
                                                status={subtask.status || 'a_faire'} // CORRECTION: Statut par défaut
                                                onChange={(value) => !disabled && onSubtaskChange(taskIndex, subtaskIndex, 'status', value)}
                                                size="small"
                                            />

                                            <input
                                                type="text"
                                                value={subtask.title}
                                                onChange={(e) => !disabled && onSubtaskChange(taskIndex, subtaskIndex, 'title', e.target.value)}
                                                disabled={disabled}
                                                className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="Décrivez cette sous-tâche..."
                                            />

                                            <button
                                                type="button"
                                                onClick={() => !disabled && onRemoveSubtask(taskIndex, subtaskIndex)}
                                                disabled={disabled}
                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    );
                                })}


                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskItem;